import {
  DatePicker,
  Field,
  Input,
  RadioGroup,
  Select,
  Spin,
} from '@/components/commons';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { OfferService } from '@/services/offer';
import { ArticleService } from '@/services/article';
import { Status, statusOptions } from '@/utils';
import { Badge, Button, Form, Image, Upload } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CloseOutlined } from '@ant-design/icons';
import 'react-quill/dist/quill.snow.css';

interface IProps {
  isLoading: boolean;
  quiz?: any;
}

export const FukumaruQuizForm: React.FC<IProps> = ({ isLoading, quiz }) => {
  const { trigger, watch, setValue, setError, clearErrors } = useFormContext();

  const [offerOptions, setOfferOptions] = useState<any[]>([]);
  const [articleOptions, setArticleOptions] = useState<any[]>([]);
  const [offerPage, setOfferPage] = useState<number>(1);
  const [hasMoreOffers, setHasMoreOffers] = useState<boolean>(true);
  const [loadingOffers, setLoadingOffers] = useState<boolean>(false);
  const OFFER_LIMIT = 20;
  const [articlePage, setArticlePage] = useState<number>(1);
  const [hasMoreArticles, setHasMoreArticles] = useState<boolean>(true);
  const [loadingArticles, setLoadingArticles] = useState<boolean>(false);
  const ARTICLE_LIMIT = 20;

  useEffect(() => {
    let canceled = false;
    const fetchOffers = async () => {
      setLoadingOffers(true);
      try {
        const res = await OfferService.getCheckboxList({
          fukumaruQuizId: quiz?.id,
        });
        const list = (res?.data || []) as any[];
        const mapped = list.map((o: any) => ({
          value: Number(o?.id ?? o?.value),
          label: String(o?.title ?? o?.label ?? ''),
        }));
        setOfferOptions((prev) => {
          const map = new Map<number | string, string>();
          prev.forEach((opt: any) => map.set(opt.value, opt.label));
          mapped.forEach((opt) => map.set(opt.value, opt.label));
          return Array.from(map.entries()).map(([value, label]) => ({
            value,
            label,
          }));
        });
      } finally {
        if (!canceled) setLoadingOffers(false);
      }
    };
    fetchOffers();
    return () => {
      canceled = true;
    };
  }, [quiz?.id]);

  useEffect(() => {
    let canceled = false;
    const fetchArticles = async () => {
      setLoadingArticles(true);
      try {
        const res = await ArticleService.getCheckboxList({
          fukumaruQuizId: quiz?.id,
        });
        const list = (res?.data || []) as any[];
        const mapped = list.map((a: any) => ({
          value: Number(a?.id ?? a?.value),
          label: String(a?.title ?? a?.label ?? ''),
        }));
        setArticleOptions((prev) => {
          const map = new Map<number | string, string>();
          prev.forEach((opt: any) => map.set(opt.value, opt.label));
          mapped.forEach((opt) => map.set(opt.value, opt.label));
          return Array.from(map.entries()).map(([value, label]) => ({
            value,
            label,
          }));
        });
      } finally {
        if (!canceled) setLoadingArticles(false);
      }
    };
    fetchArticles();
    return () => {
      canceled = true;
    };
  }, [quiz?.id]);

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(dayjs().startOf('day'));
  };

  const disabledTime = (current: Dayjs) => {
    if (!current) return {};
    const now = dayjs();
    const selectedDate = dayjs(current);
    if (selectedDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }
    if (selectedDate.isSame(now, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < now.hour(); i++) {
        disabledHours.push(i);
      }
      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = [];
          for (let i = 0; i <= now.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };
      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };

  const coverImage: any | null = watch('coverImage');
  const isDisabled = watch('isDisabled');
  const status = watch('status');
  const formName = watch('formName');

  const beforeUpload = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > FILE_SIZE_LIMIT) {
      setError('attachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }
    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('attachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('attachment');
    handleUpload(file);
    return true;
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setValue('coverImage', {
        previewUrl: reader.result as string,
        file,
      });
    };
    reader.readAsDataURL(file);
  };

  const isPublic = useMemo(
    () =>
      !!quiz &&
      quiz.status === Status.PUBLIC &&
      moment(quiz.publicAt).isSameOrBefore(moment()),
    [quiz],
  );

  useEffect(() => {
    if (isPublic && status === Status.PUBLIC && quiz?.publicAt) {
      setValue('publicAt', moment(quiz.publicAt));
    }
  }, [status, isPublic, quiz, setValue]);

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading}>
        <label className='title-calendar font-semibold'>
          カバー画像
          <Badge status='error' className='ml-2 mb-1' />
        </label>
        <div className='flex items-baseline gap-2 pt-2'>
          {!coverImage ? (
            <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                beforeUpload={(file: File) => beforeUpload(file)}
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                showUploadList={false}
              >
                <Button
                  type='default'
                  shape='default'
                  size='middle'
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                  disabled={isDisabled}
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          ) : (
            <div className='relative inline-block'>
              <Image
                height={100}
                src={coverImage?.previewUrl}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={() => setValue('coverImage', null)}
                className={`absolute -top-2 -right-2 z-10 bg-red-500 border-none ${isDisabled && 'hidden'}`}
              />
            </div>
          )}
        </div>
        <label className='title-calendar font-medium text-xs'>
          <span className='font-bold mr-1'>※</span>
          1.8:1(~375×211 px)の写真（正方形）をアップロードしてください
        </label>
        <Field name='attachment' className='mt-[-34px]'>
          <></>
        </Field>

        <Field label='タイトル' name='title' required>
          <Input placeholder='タイトル' maxLength={100} disabled={isDisabled} />
        </Field>

        <Field label='本文' name='content' required>
          <Input placeholder='本文' disabled={isDisabled} />
        </Field>

        <Field label='掲載オファー' name='offerIds'>
          <Select
            mode='multiple'
            placeholder='掲載オファー'
            style={{ width: '100%' }}
            options={offerOptions}
            loading={loadingOffers}
            onPopupScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (
                target.scrollTop + target.offsetHeight >=
                  target.scrollHeight - 32 &&
                hasMoreOffers &&
                !loadingOffers
              ) {
                setOfferPage((p) => p + 1);
              }
            }}
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            disabled={isDisabled}
          />
        </Field>

        <Field label='掲載記事' name='articleIds'>
          <Select
            mode='multiple'
            placeholder='掲載記事'
            style={{ width: '100%' }}
            options={articleOptions}
            loading={loadingArticles}
            onPopupScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (
                target.scrollTop + target.offsetHeight >=
                  target.scrollHeight - 32 &&
                hasMoreArticles &&
                !loadingArticles
              ) {
                setArticlePage((p) => p + 1);
              }
            }}
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            disabled={isDisabled}
          />
        </Field>

        <div className='flex items-baseline gap-2'>
          <div className='flex-1'>
            <span className='title-calendar font-semibold'>
              公開日時
              <Badge status='error' className='ml-2' />
            </span>
          </div>
          <div className='flex-1'>
            <span className='title-calendar font-semibold ml-2'>
              公開終了日時
            </span>
          </div>
        </div>

        <div className='flex items-baseline gap-1 pt-2'>
          <Field name='publicAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('publicAt', e ? (e as Moment).second(0) : undefined);
                trigger('publicAt');
                trigger('publicEndAt');
              }}
              disabled={isDisabled || (isPublic && status !== Status.DRAFT)}
            />
          </Field>
          <span>～</span>
          <Field name='publicEndAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue(
                  'publicEndAt',
                  e ? (e as Moment).second(0) : undefined,
                );
                trigger('publicEndAt');
              }}
              disabled={isDisabled}
            />
          </Field>
        </div>

        <Field label='公開ステータス' name='status' className='w-full'>
          <RadioGroup options={statusOptions} disabled={isDisabled} />
        </Field>
      </Spin>
    </Form>
  );
};
